import { useEffect, useRef, useState } from "react";
import { cameraProfile, SCREEN_PROFILE } from "@/lib/videoBudget";
import AgoraRTC from "agora-rtc-sdk-ng";
import { Users, MicOff, Volume2, LogOut, Mic, Video, VideoOff, Send, MessageCircle, X, MonitorUp } from "lucide-react";

function mediaErrorMessage(e, what) {
  const n = `${e?.name || ""} ${e?.code || ""} ${e?.message || ""}`;
  if (/NotAllowed|PERMISSION_DENIED/i.test(n))
    return `El navegador tiene bloqueado ${what}. Pulsa el candado junto a la dirección, permite ${what} y vuelve a intentarlo.`;
  if (/NotReadable|NOT_READABLE/i.test(n))
    return `Otra aplicación o pestaña está usando ${what}. Ciérrala y vuelve a intentarlo.`;
  if (/NotFound|DEVICES_NOT_FOUND/i.test(n))
    return `No se encontró ${what} en este equipo.`;
  return `No se pudo activar ${what}. Intenta de nuevo.`;
}

function GuestRemoteUser({ user, name }) {
  const ref = useRef(null);
  useEffect(() => {
    if (user.videoTrack && ref.current) {
      user.videoTrack.play(ref.current, { fit: "contain" });
    }
  }, [user.videoTrack]);
  useEffect(() => {
    if (user.audioTrack) user.audioTrack.play();
  }, [user.audioTrack]);

  return (
    <div className="relative w-full h-full bg-[#1A1D2E] rounded-xl overflow-hidden border border-white/10" data-testid={`guest-remote-${user.uid}`}>
      <div ref={ref} className="absolute inset-0" />
      {!user.videoTrack && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-[#A43790]/20 flex items-center justify-center">
            <Users size={24} className="text-[#A43790]" />
          </div>
        </div>
      )}
      <div className="absolute bottom-1.5 left-1.5 bg-black/60 backdrop-blur-sm rounded-lg px-2 py-0.5 flex items-center gap-1.5">
        {user.audioTrack ? <Volume2 size={10} className="text-[#3AE880]" /> : <MicOff size={10} className="text-red-400" />}
        <span className="text-[10px] font-bold text-white/80">{name || "Participante"}</span>
      </div>
    </div>
  );
}

export default function GuestVideoView({ appId, token, channelName, uid, guestName, guestId, onLeave }) {
  const [allRemoteUsers, setRemoteUsers] = useState([]);
  const [error, setError] = useState(null);
  const [sharingUid, setSharingUid] = useState(null);
  const [identities, setIdentities] = useState({});
  const [screenOn, setScreenOn] = useState(false);
  const [canShare, setCanShare] = useState(false); // lo decide el docente
  const screenTrackRef = useRef(null);
  const announceRef = useRef(null);
  const stopScreenRef = useRef(null);
  // Solo mostramos participantes reales: los que publican audio/video o ya se
  // identificaron por el canal. Evita "fantasmas" (sesiones que quedaron colgadas
  // en Agora, el bot de grabación, etc.) que solo disparan user-joined.
  const remoteUsers = allRemoteUsers.filter((u) => u.videoTrack || u.audioTrack || identities[u.uid]);

  // v5.3.0 · Latido de consumo (la sesión de uso del invitado usa su guestId): cada 60 s y al salir/cerrar.
  const guestHasVideoRef = useRef(false);
  guestHasVideoRef.current = remoteUsers.some((u) => u.videoTrack);
  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/video/usage/ping`;
    const send = (left = false, beacon = false) => {
      const body = JSON.stringify({ session: guestId, video: guestHasVideoRef.current, left });
      try {
        if (beacon && navigator.sendBeacon) { navigator.sendBeacon(url, new Blob([body], { type: "application/json" })); return; }
        fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
      } catch { /* sin medición */ }
    };
    const iv = setInterval(() => send(false), 60000);
    const onHide = () => send(true, true);
    window.addEventListener("pagehide", onHide);
    return () => { clearInterval(iv); window.removeEventListener("pagehide", onHide); send(true); };
  }, [guestId]);
  const [micOn, setMicOn] = useState(false);
  const [camOn, setCamOn] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [unreadChat, setUnreadChat] = useState(0);
  const chatOpenRef = useRef(false);
  chatOpenRef.current = chatOpen;
  useEffect(() => { if (chatOpen) setUnreadChat(0); }, [chatOpen]);
  const [chatInput, setChatInput] = useState("");
  const clientRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const localContainerRef = useRef(null);
  const controlWsRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);

  useEffect(() => {
    if (camOn && localVideoTrackRef.current && localContainerRef.current) {
      localVideoTrackRef.current.play(localContainerRef.current, { fit: "contain" });
    }
  }, [camOn]);

  useEffect(() => {
    const activeRef = { current: true };

    const join = async () => {
      try {
        const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
        clientRef.current = client;

        client.on("user-published", async (user, mediaType) => {
          if (!activeRef.current) return;
          let subscribed = false;
          for (let attempt = 1; attempt <= 3; attempt++) {
            try {
              await client.subscribe(user, mediaType);
              subscribed = true;
              break;
            } catch (e) {
              if (attempt < 3) await new Promise((r) => setTimeout(r, 800 * attempt));
            }
          }
          if (!subscribed || !activeRef.current) return;
          setRemoteUsers((prev) => {
            const exists = prev.find((u) => u.uid === user.uid);
            if (exists) return prev.map((u) => u.uid === user.uid ? { ...u, [mediaType + "Track"]: user[mediaType + "Track"] } : u);
            return [...prev, { uid: user.uid, videoTrack: user.videoTrack, audioTrack: user.audioTrack }];
          });
        });

        client.on("user-unpublished", (user, mediaType) => {
          if (!activeRef.current) return;
          setRemoteUsers((prev) =>
            prev.map((u) => u.uid === user.uid ? { ...u, [mediaType + "Track"]: null } : u)
          );
        });

        // Quien entra sin publicar audio/video también debe verse en la lista.
        client.on("user-joined", (user) => {
          if (!activeRef.current) return;
          setRemoteUsers((prev) =>
            prev.find((u) => u.uid === user.uid) ? prev : [...prev, { uid: user.uid, videoTrack: null, audioTrack: null }]
          );
        });

        client.on("user-left", (user) => {
          if (!activeRef.current) return;
          setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));
        });

        await client.join(appId, channelName, token, uid);
        if (!activeRef.current) {
          await client.leave().catch(() => {});
          return;
        }

        // WebSocket para chat
        const wsBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
        const ws = new WebSocket(`${wsBase}/api/ws/whiteboard/${channelName}?guest_id=${encodeURIComponent(guestId)}`);
        controlWsRef.current = ws;
        const announce = () => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({
              type: "presence",
              data: { kind: "agora_identify", agora_uid: uid, name: guestName || "Invitado" },
            }));
          }
        };
        ws.onopen = () => {
          announce();
          const iv = setInterval(announce, 8000);
          ws.addEventListener("close", () => clearInterval(iv));
        };
        ws.onmessage = (ev) => {
          let msg; try { msg = JSON.parse(ev.data); } catch { return; }
          if (msg.type === "control" && msg.action === "screen_share_permission") {
            const allowed = !!msg.data?.allowed;
            setCanShare(allowed);
            if (!allowed && screenTrackRef.current) stopScreenRef.current?.();
          }
          if (msg.type === "class_ended") {
            setError("El docente finalizó la clase.");
            setTimeout(() => onLeave?.(), 2500);
          }
          if (msg.type === "chat_history" && Array.isArray(msg.data)) {
            setChatMessages(msg.data.slice(-50).map((m, i) => ({
              id: `h-${m.ts}-${i}`, name: m.name || "Alguien", text: m.text, mine: m.user_id === guestId,
            })));
          }
          if (msg.type === "presence" && msg.data?.kind === "agora_identify" && msg.data.agora_uid && msg.data.name) {
            setIdentities((prev) => ({ ...prev, [msg.data.agora_uid]: { name: msg.data.name } }));
          }
          if (msg.type === "presence" && msg.data?.kind === "screen_share_started") {
            // Si otra persona empieza a compartir, dejo de compartir yo.
            if (screenTrackRef.current && msg.data.agora_uid !== uid) stopScreenRef.current?.();
            setSharingUid(msg.data.agora_uid);
          }
          if (msg.type === "presence" && msg.data?.kind === "screen_share_stopped") {
            setSharingUid((prev) => (prev === msg.data.agora_uid ? null : prev));
          }
          if (msg.type === "presence" && msg.data?.kind === "chat_message") {
            if (msg.data.agora_uid !== uid && !chatOpenRef.current) setUnreadChat((n) => n + 1);
            setChatMessages((prev) => [...prev.slice(-49), {
              id: `${msg.data.agora_uid}-${msg.data.ts}`,
              name: msg.data.name || "Alguien", text: msg.data.text,
              mine: msg.data.agora_uid === uid,
            }]);
          }
        };
      } catch (e) {
        console.error("[GuestVideoView] join error:", e);
        if (activeRef.current) {
          const raw = `${e?.code || ""} ${e?.message || ""}`;
          setError(/CAN_NOT_GET_GATEWAY_SERVER|GATEWAY|NETWORK/i.test(raw)
            ? "No se pudo conectar al servicio de video. Revisa tu internet (o desactiva VPN/bloqueadores) e intenta de nuevo."
            : "No se pudo conectar a la clase.");
        }
      }
    };

    join();

    return () => {
      activeRef.current = false;
      controlWsRef.current?.close();
      controlWsRef.current = null;
      localAudioTrackRef.current?.close();
      localVideoTrackRef.current?.close();
      screenTrackRef.current?.close();
      screenTrackRef.current = null;
      if (announceRef.current) { clearInterval(announceRef.current); announceRef.current = null; }
      const c = clientRef.current;
      if (c) {
        c.leave().catch(() => {});
        clientRef.current = null;
      }
    };
  }, [appId, token, channelName, uid, guestId]);

  const toggleMic = async () => {
    const client = clientRef.current;
    if (!client) return;
    if (!micOn) {
      if (!localAudioTrackRef.current) {
        try {
          localAudioTrackRef.current = await AgoraRTC.createMicrophoneAudioTrack({ encoderConfig: "speech_standard", AEC: true, ANS: true, AGC: true });
          await client.publish(localAudioTrackRef.current);
        } catch (e) { console.error("No se pudo activar el micrófono:", e); setError(mediaErrorMessage(e, "el micrófono")); return; }
      } else {
        localAudioTrackRef.current.setEnabled(true);
      }
      setMicOn(true);
    } else {
      localAudioTrackRef.current?.setEnabled(false);
      setMicOn(false);
    }
  };

  const toggleCam = async () => {
    const client = clientRef.current;
    if (!client) return;
    if (!camOn) {
      if (!localVideoTrackRef.current) {
        try {
          localVideoTrackRef.current = await AgoraRTC.createCameraVideoTrack({ encoderConfig: cameraProfile(3) });
          await client.publish(localVideoTrackRef.current);
        } catch (e) { console.error("No se pudo activar la cámara:", e); setError(mediaErrorMessage(e, "la cámara")); return; }
      } else {
        localVideoTrackRef.current.setEnabled(true);
      }
      setCamOn(true);
    } else {
      localVideoTrackRef.current?.setEnabled(false);
      setCamOn(false);
    }
  };

  const stopScreen = async () => {
    const client = clientRef.current;
    if (screenTrackRef.current) {
      if (client) await client.unpublish(screenTrackRef.current).catch(() => {});
      screenTrackRef.current.close();
      screenTrackRef.current = null;
    }
    if (announceRef.current) { clearInterval(announceRef.current); announceRef.current = null; }
    if (localVideoTrackRef.current && client) await client.publish(localVideoTrackRef.current).catch(() => {});
    setScreenOn(false);
    const ws = controlWsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "presence", data: { kind: "screen_share_stopped", agora_uid: uid } }));
    }
  };
  stopScreenRef.current = stopScreen;

  const toggleScreen = async () => {
    if (screenOn) { await stopScreen(); return; }
    const client = clientRef.current;
    if (!client) return;
    if (!canShare) { setError("El docente no ha habilitado compartir pantalla."); return; }
    if (!navigator.mediaDevices?.getDisplayMedia) {
      setError("Compartir pantalla no está disponible en este dispositivo.");
      return;
    }
    try {
      const created = await AgoraRTC.createScreenVideoTrack({ encoderConfig: SCREEN_PROFILE, optimizationMode: "detail" }, "disable");
      const screen = Array.isArray(created) ? created[0] : created;
      screenTrackRef.current = screen;
      if (localVideoTrackRef.current) await client.unpublish(localVideoTrackRef.current).catch(() => {});
      await client.publish(screen);
      screen.on("track-ended", () => { stopScreenRef.current?.(); });
      setScreenOn(true);
      const announce = () => {
        const ws = controlWsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "presence", data: { kind: "screen_share_started", agora_uid: uid } }));
        }
      };
      announce();
      if (announceRef.current) clearInterval(announceRef.current);
      announceRef.current = setInterval(announce, 4000);
    } catch (e) {
      console.warn("[GuestVideoView] compartir pantalla falló:", e);
      screenTrackRef.current = null;
      if (!/NotAllowed|PERMISSION_DENIED/i.test(`${e?.name} ${e?.code}`)) setError("No se pudo compartir la pantalla.");
    }
  };

  const sendChatMessage = () => {
    const text = chatInput.trim();
    if (!text || !controlWsRef.current) return;
    controlWsRef.current.send(JSON.stringify({
      type: "presence",
      data: { kind: "chat_message", agora_uid: uid, name: guestName, text, ts: Date.now() },
    }));
    setChatMessages((prev) => [...prev.slice(-49), { id: `${uid}-${Date.now()}`, name: "Tú", text, mine: true }]);
    setChatInput("");
  };

  const totalUsers = remoteUsers.length;
  const gridClass = totalUsers <= 1 ? "grid-cols-1" :
    totalUsers <= 4 ? "grid-cols-2" :
    totalUsers <= 9 ? "grid-cols-3" : "grid-cols-4";

  return (
    <div className="flex flex-col h-screen bg-[#0d1025] relative" data-testid="guest-video-view">
      {/* Video grid */}
      <div className="flex-1 flex flex-col gap-2 p-2 min-h-0">
        {remoteUsers.length === 0 && !error && (
          <div className="flex-1 flex items-center justify-center text-white/40 text-sm">
            Esperando que los participantes se conecten…
          </div>
        )}
        {remoteUsers.length > 0 && !sharingUid && (
          <div className={`flex-1 grid ${gridClass} gap-2 auto-rows-fr min-h-0`}>
            {remoteUsers.map((u) => (<GuestRemoteUser key={u.uid} user={u} name={identities[u.uid]?.name} />))}
          </div>
        )}
        {remoteUsers.length > 0 && sharingUid && (() => {
          const featured = remoteUsers.find((u) => String(u.uid) === String(sharingUid));
          const others = remoteUsers.filter((u) => String(u.uid) !== String(sharingUid));
          if (!featured) {
            console.warn("[GuestVideoView] sharingUid no coincide con ningún remoteUser:",
              sharingUid, "uids disponibles:", remoteUsers.map((u) => ({ uid: u.uid, hasVideo: !!u.videoTrack })));
          }
          return (
            <>
              <div className="flex-1 min-h-0">
                {featured ? (
                  <div className="w-full h-full">
                    <GuestRemoteUser user={featured} name={identities[featured.uid]?.name} />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/40 text-sm">Cargando pantalla compartida…</div>
                )}
              </div>
              <div className="flex gap-2 h-24 overflow-x-auto shrink-0">
                {others.map((u) => (
                  <div key={u.uid} className="w-32 h-full shrink-0"><GuestRemoteUser user={u} name={identities[u.uid]?.name} /></div>
                ))}
              </div>
            </>
          );
        })()}
      </div>

      {/* Local video thumbnail */}
      {camOn && (
        <div ref={localContainerRef} className="absolute bottom-20 right-3 w-32 h-24 rounded-lg overflow-hidden bg-[#1A1D2E] border border-white/20 z-10" />
      )}

      {error && (
        <div className="px-4 py-2 bg-red-500/20 border-t border-red-500/30 text-center">
          <p className="text-xs text-red-300">{error}</p>
        </div>
      )}

      {/* Chat panel */}
      {chatOpen && (
        <div className="absolute top-0 right-0 bottom-0 w-72 bg-[#12152B]/95 backdrop-blur-md border-l border-white/10 flex flex-col z-30" data-testid="guest-chat-panel">
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
            <span className="text-xs font-bold text-white/80 flex items-center gap-1.5"><MessageCircle size={13} /> Chat de clase</span>
            <button onClick={() => setChatOpen(false)} className="text-white/40 hover:text-white"><X size={16} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2 min-h-0">
            {chatMessages.length === 0 && <p className="text-[11px] text-white/30 text-center pt-4">Sin mensajes aún</p>}
            {chatMessages.map((m) => (
              <div key={m.id} className={`flex flex-col ${m.mine ? "items-end" : "items-start"}`}>
                <span className="text-[10px] font-bold text-white/40 mb-0.5">{m.mine ? "Tú" : m.name}</span>
                <span className={`text-xs px-2.5 py-1.5 rounded-xl max-w-[200px] break-words ${m.mine ? "bg-[#A43790] text-white" : "bg-white/10 text-white/90"}`}>{m.text}</span>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <div className="px-2 py-2 border-t border-white/10 flex gap-1.5">
            <input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
              placeholder="Escribe…"
              maxLength={300}
              className="flex-1 bg-white/10 text-white text-xs rounded-lg px-3 py-2 outline-none placeholder-white/30"
              data-testid="guest-chat-input"
            />
            <button onClick={sendChatMessage} className="w-8 h-8 rounded-lg bg-[#A43790] hover:bg-[#C44DB0] flex items-center justify-center text-white" data-testid="guest-chat-send"><Send size={14} /></button>
          </div>
        </div>
      )}

      {/* Controls bar */}
      <div className="flex items-center justify-center gap-3 py-3 bg-[#12152B] border-t border-white/10">
        <div className="bg-black/40 px-3 py-1 rounded-lg">
          <span className="text-xs text-white/60">{guestName} (Invitado)</span>
        </div>

        <button onClick={toggleMic} data-testid="guest-toggle-mic"
          className={`w-11 h-11 rounded-full flex items-center justify-center text-white transition-colors ${micOn ? "bg-white/10 hover:bg-white/20" : "bg-red-500/80 hover:bg-red-600"}`}
          title={micOn ? "Apagar micrófono" : "Activar micrófono"}>
          {micOn ? <Mic size={18} /> : <MicOff size={18} />}
        </button>
        <button onClick={toggleCam} data-testid="guest-toggle-cam"
          className={`w-11 h-11 rounded-full flex items-center justify-center text-white transition-colors ${camOn ? "bg-white/10 hover:bg-white/20" : "bg-white/10 hover:bg-white/20"}`}
          title={camOn ? "Apagar cámara" : "Activar cámara"}>
          {camOn ? <Video size={18} /> : <VideoOff size={18} />}
        </button>
        {canShare && (
          <button onClick={toggleScreen} data-testid="guest-toggle-screen"
            className={`w-11 h-11 rounded-full flex items-center justify-center text-white transition-colors ${screenOn ? "bg-[#A43790]" : "bg-white/10 hover:bg-white/20"}`}
            title={screenOn ? "Dejar de compartir" : "Compartir pantalla"}>
            <MonitorUp size={18} />
          </button>
        )}
        <button onClick={() => setChatOpen((v) => !v)} data-testid="guest-toggle-chat"
          className={`relative w-11 h-11 rounded-full flex items-center justify-center text-white transition-colors ${chatOpen ? "bg-[#A43790]" : "bg-white/10 hover:bg-white/20"}`}
          title="Chat">
          <MessageCircle size={18} />
          {unreadChat > 0 && !chatOpen && (
            <span data-testid="guest-chat-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center ring-2 ring-[#0d1025] animate-pulse">
              {unreadChat > 9 ? "9+" : unreadChat}
            </span>
          )}
        </button>

        <button
          onClick={onLeave}
          data-testid="guest-leave-btn"
          className="w-11 h-11 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors"
          title="Salir"
        >
          <LogOut size={18} />
        </button>
      </div>
    </div>
  );
}
